(() => {
  'use strict';

  const DB_NAME = window.historyDB || '3dMockupShaped_v2';
  const DB_VERSION = 1;
  const STORE_NAME = 'allaboutvibe';
  const MAX_HISTORY_SIZE = 10;

  const EVENTS = {
    CHANGED: 'fileHistoryChanged',
    READY: 'fileHistoryManagerReady',
    ERROR: 'fileHistoryManagerError'
  };

  const FileHistoryManager = (function() {
    let db = null;
    let inMemoryHistory = [];
    let config = {
      saveToIndexedDB: true
    };
    let isInitialized = false;

    // ============================================================================
    // IndexedDB Management
    // ============================================================================
    function initIndexedDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        
        request.onerror = () => {
          console.warn('[FileHistoryManager] IndexedDB error:', request.error);
          reject(request.error);
        };
        
        request.onsuccess = () => {
          db = request.result;
          loadFromIndexedDB()
            .then(() => resolve(db))
            .catch(reject);
        };
        
        request.onupgradeneeded = (event) => {
          const database = event.target.result;
          
          if (!database.objectStoreNames.contains(STORE_NAME)) {
            const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
            store.createIndex('currentFile', 'currentFile', { unique: false });
          }
        };
      });
    }

    function loadFromIndexedDB() {
      return new Promise((resolve, reject) => {
        if (!db) {
          resolve();
          return;
        }
        
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();
        
        request.onsuccess = () => {
          const loaded = request.result || [];
          // Sort by createdAt descending (newest first), or by currentFile if no timestamp
          inMemoryHistory = loaded.sort((a, b) => {
            // Current file first
            if (a.currentFile && !b.currentFile) return -1;
            if (!a.currentFile && b.currentFile) return 1;
            // Then by createdAt (newest first)
            const timeA = a.createdAt || 0;
            const timeB = b.createdAt || 0;
            return timeB - timeA;
          });
          resolve();
        };
        
        request.onerror = () => {
          console.warn('[FileHistoryManager] Error loading from IndexedDB:', request.error);
          reject(request.error);
        };
      });
    }

    function saveToIndexedDB(history) {
      return new Promise((resolve, reject) => {
        if (!db) {
          resolve();
          return;
        }
        
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        
        store.clear();
        
        history.forEach(file => {
          store.add(file);
        });
        
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => {
          console.warn('[FileHistoryManager] Error saving to IndexedDB:', transaction.error);
          reject(transaction.error);
        };
      });
    }

    function clearIndexedDB() {
      if (!db) return;
      
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.clear();
    }

    // ============================================================================
    // Configuration
    // ============================================================================
    function setConfig(newConfig) {
      config = { ...config, ...newConfig };
      
      if (!config.saveToIndexedDB && db) {
        clearIndexedDB();
      }
      
      if (config.saveToIndexedDB && !db && isInitialized) {
        initIndexedDB().catch(error => {
          console.warn('[FileHistoryManager] Failed to init IndexedDB:', error);
        });
      }
    }

    // ============================================================================
    // History Management
    // ============================================================================
    function getHistory() {
      return [...inMemoryHistory];
    }

    function saveHistory(history) {
      const limitedHistory = history.slice(0, MAX_HISTORY_SIZE);
      
      inMemoryHistory = limitedHistory;
      
      if (config.saveToIndexedDB && db) {
        saveToIndexedDB(limitedHistory).catch(error => {
          console.warn('[FileHistoryManager] Failed to save to IndexedDB:', error);
        });
      }
    }

    function dispatchEvent(action, data) {
      window.dispatchEvent(new CustomEvent(EVENTS.CHANGED, {
        detail: { action, ...data }
      }));
    }

    // ============================================================================
    // File ID Generation
    // ============================================================================
    function generateFileOriginalId(name, size, lastModified) {
      if (!name || typeof size !== 'number' || typeof lastModified !== 'number') {
        return null;
      }
      return `${name}_${size}_${lastModified}`;
    }

    function generateFileId(name, size, lastModified, color) {
      const originalId = generateFileOriginalId(name, size, lastModified);
      if (!originalId || !color) return null;
      return `${originalId}_${color}`;
    }

    // ============================================================================
    // File Operations
    // ============================================================================
    function getCurrentFile() {
      const history = getHistory();
      return history.find(f => f.currentFile === true) || null;
    }

    function setCurrentFile(fileId) {
      const history = getHistory();
      let changed = false;
      
      history.forEach(f => {
        if (f.currentFile && f.id !== fileId) {
          f.currentFile = false;
          changed = true;
        } else if (!f.currentFile && f.id === fileId) {
          f.currentFile = true;
          changed = true;
        }
      });
      
      if (changed) {
        saveHistory(history);
        dispatchEvent('currentChanged', {
          currentFileId: fileId
        });
      }
      
      return history.find(f => f.id === fileId) || null;
    }

    function getFileFromHistory(fileId) {
      const history = getHistory();
      return history.find(f => f.id === fileId) || null;
    }

    function getFileFromHistoryByUrl(s3Url, color) {
      const history = getHistory();
      return history.find(f => f.s3Url === s3Url && f.color === color) || null;
    }

    function getFileByOriginalId(originalFileId) {
      if (!originalFileId) return null;
      const history = getHistory();
      return history.find(f => {
        const storedOriginalId = f.fileOriginalId;
        const extractedOriginalId = f.id && f.color
          ? f.id.endsWith(`_${f.color}`) ? f.id.slice(0, -(`_${f.color}`.length)) : null
          : null;
        return storedOriginalId === originalFileId || extractedOriginalId === originalFileId;
      }) || null;
    }

    function isFileInHistory(fileOriginalId, url = null, color = null) {
      const history = getHistory();
      
      if (fileOriginalId && color) {
        const fileId = `${fileOriginalId}_${color}`;
        if (fileId && history.some(f => f.id === fileId)) {
          return true;
        }
      }
      
      if (url && color) {
        return history.some(f => f.s3Url === url && f.color === color);
      }
      
      return false;
    }

    function createHistoryItemObject(data) {
      return {
        id: data.id,
        fileOriginalId: data.fileOriginalId,
        color: data.color,
        s3Url: data.s3Url,
        preview: data.preview,
        previewWithBg: data.previewWithBg || data.preview,
        pillowData: data.pillowData,
        currentFile: true,
        createdAt: data.createdAt || Date.now() // Track creation time for sorting
      };
    }

    function createHistoryItem(data) {
      if (!data || typeof data !== 'object') {
        console.warn('[FileHistoryManager] createHistoryItem: data object is required');
        return null;
      }

      const requiredFields = ['id', 'fileOriginalId', 'color', 'preview', 's3Url', 'pillowData', 'previewWithBg'];
      for (const field of requiredFields) {
        if (!data[field]) {
          console.warn(`[FileHistoryManager] createHistoryItem: ${field} is required`);
          return null;
        }
      }

      const existingItem = getFileFromHistory(data.id);
      if (existingItem) {
        // Update timestamp to move to top when reused
        existingItem.createdAt = Date.now();
        existingItem.currentFile = true;
        setCurrentFile(data.id);
        // Re-sort to maintain order
        const history = getHistory();
        history.sort((a, b) => {
          if (a.currentFile && !b.currentFile) return -1;
          if (!a.currentFile && b.currentFile) return 1;
          const timeA = a.createdAt || 0;
          const timeB = b.createdAt || 0;
          return timeB - timeA;
        });
        saveHistory(history);
        return existingItem;
      }

      const history = getHistory();
      const historyItem = createHistoryItemObject(data);

      const existingIndex = history.findIndex(f => f.id === historyItem.id);
      if (existingIndex !== -1) {
        history.splice(existingIndex, 1);
      }

      history.forEach(f => f.currentFile = false);
      history.unshift(historyItem);

      saveHistory(history);
      dispatchEvent('added', { fileInfo: historyItem });

      return historyItem;
    }

    function useFromHistory(fileOriginalId, fileUrl = null, color = nulll) {
      if (!color) {
        return { fileId: null, s3Url: null, pillowData: null, preview: null, previewWithBg: null };
      }

      const isInHistory = isFileInHistory(fileOriginalId, fileUrl, color);
      let result = { fileId: null, s3Url: null, pillowData: null, preview: null, previewWithBg: null };
      
      if (!isInHistory && fileOriginalId) {
          const fileInfoByOriginalId = getFileByOriginalId(fileOriginalId);
          if (fileInfoByOriginalId) {
            result = {
              fileId: null,
              s3Url: fileInfoByOriginalId.s3Url || null,
              pillowData: null,
              preview: null,
              previewWithBg: null,
              fileOriginalId: fileInfoByOriginalId.fileOriginalId || null
            };
          } 
      } else {
        let fileInfo = null;

        if (fileOriginalId) {
          const fileId = `${fileOriginalId}_${color}`;
          fileInfo = getFileFromHistory(fileId);
        } else if (fileUrl) {
          fileInfo = getFileFromHistoryByUrl(fileUrl, color);
        }

        if (fileInfo) {
          result = {
            fileId: fileInfo.id || null,
            s3Url: fileInfo.s3Url || null,
            pillowData: fileInfo.pillowData || null,
            preview: fileInfo.preview || null,
            previewWithBg: fileInfo.previewWithBg || null,
            fileOriginalId: fileInfo.fileOriginalId || null
          };
        }
      }

      if (result.fileId) {
        setCurrentFile(result.fileId);
      }
      
      return result;
    }

    // ============================================================================
    // Initialization
    // ============================================================================
    function init() {
      if (isInitialized) return;
      
      if (config.saveToIndexedDB) {
        initIndexedDB()
          .then(() => {
            isInitialized = true;
            window.dispatchEvent(new CustomEvent(EVENTS.READY, {
              detail: {
                manager: 'FileHistoryManager',
                status: 'ready',
                hasIndexedDB: true,
                historyCount: inMemoryHistory.length
              }
            }));
          })
          .catch(error => {
            console.warn('[FileHistoryManager] Initialization failed:', error);
            window.dispatchEvent(new CustomEvent(EVENTS.ERROR, {
              detail: {
                manager: 'FileHistoryManager',
                status: 'error',
                error: error.message,
                hasIndexedDB: false
              }
            }));
          });
      } else {
        isInitialized = true;
        window.dispatchEvent(new CustomEvent(EVENTS.READY, {
          detail: {
            manager: 'FileHistoryManager',
            status: 'ready',
            hasIndexedDB: false,
            historyCount: inMemoryHistory.length
          }
        }));
      }
    }
     // Clear all history
     function clearHistory() {
      try {
          if (saveToIndexedDB && db) {
              try {
                  clearIndexedDB();
              } catch (error) {
                  console.warn('Error clearing IndexedDB:', error);
              }
          }
          if (saveInMemory) {
              inMemoryHistory = [];
          }
      } catch (error) {
          console.warn('clearHistory error:', error.message);
      }
  }

    return {
      setConfig,
      init,
      getHistory,
      getCurrentFile,
      setCurrentFile,
      getFileFromHistory,
      getFileFromHistoryByUrl,
      getFileByOriginalId,
      isFileInHistory,
      useFromHistory,
      createHistoryItem,
      clearHistory
    };
  })();

  // ============================================================================
  // Auto-initialize
  // ============================================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => FileHistoryManager.init());
  } else {
    FileHistoryManager.init();
  }

  // ============================================================================
  // Expose API
  // ============================================================================
  window.FileHistoryManager = FileHistoryManager;
  window.FileHistoryManager.EVENTS = EVENTS;

  window.ShapedFileHistory = {
    setConfig: (config) => FileHistoryManager.setConfig(config),
    init: () => FileHistoryManager.init(),
    getHistory: () => FileHistoryManager.getHistory(),
    getCurrentFile: () => FileHistoryManager.getCurrentFile(),
    setCurrentFile: (fileId) => FileHistoryManager.setCurrentFile(fileId),
    getFileFromHistory: (fileId) => FileHistoryManager.getFileFromHistory(fileId),
    getFileFromHistoryByUrl: (fileUrl, color) => FileHistoryManager.getFileFromHistoryByUrl(fileUrl, color),
    getFileByOriginalId: (originalFileId) => FileHistoryManager.getFileByOriginalId(originalFileId),
    isFileInHistory: (file, fileUrl, color) => FileHistoryManager.isFileInHistory(file, fileUrl, color),
    useFromHistory: (fileOriginalId, fileUrl, color) => FileHistoryManager.useFromHistory(fileOriginalId, fileUrl, color),
    createHistoryItem: (data) => FileHistoryManager.createHistoryItem(data),
    clearHistory: () => FileHistoryManager.clearHistory()
  };
})();
